import { useCallback, useMemo, useRef, useState } from "react";
import { CommentReplies } from "components/common/CommentReplies";
import { UnifiedThreadComment } from "components/common/UnifiedThreadComment";
import { Separator } from "components/elements";
import { ThreadCommentForm } from "components/form/ThreadCommentForm";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useUserTimezone } from "hooks/useUserTimezone";
import { cn } from "lib/cn";
import { formatFullTimestamp, formatRelativeTime } from "lib/relativeTime";
import type { ApiThreadComment } from "lib/threadCommentUtils";
import { trpc } from "lib/trpc";

type ParticipantRef = {
    id: number;
    name: string;
};

export type ParticipantEvent = {
    user: ParticipantRef;
    addedAt: string | null;
    addedBy: ParticipantRef | null;
    removedAt: string | null;
    removedBy: ParticipantRef | null;
};

export interface DiscussionCommentsProps {
    className?: string;
    /** Thread comments from the API */
    comments: ApiThreadComment[];
    /** Discussion ID for building share URLs */
    discussionId?: number;
    /** Thread ID for creating replies */
    threadId?: number;
    /** Full participant history for synthesizing add/remove timeline events */
    participantEvents?: ParticipantEvent[];
}

/**
 * Discussion Comments Component
 * Renders comments for a discussion using the unified thread comment system
 */
type ParticipantChange = {
    who: ParticipantRef;
    by: ParticipantRef | null;
    at: number;
    iso: string;
};

type TimelineItem =
    | { kind: "comment"; at: number; comment: ApiThreadComment }
    | {
          kind: "added" | "removed";
          at: number;
          changes: ParticipantChange[];
          key: string;
      };

type RawEvent = {
    kind: "added" | "removed";
    at: number;
    iso: string;
    who: ParticipantRef;
    by: ParticipantRef | null;
};

// Group consecutive add/remove events of the same kind so a burst of "X added,
// Y added, Z added" between two messages collapses into one chip. We only
// merge runs of the same kind — an add followed by a remove stays as two chips.
const buildTimeline = (
    comments: ApiThreadComment[],
    events: ParticipantEvent[],
): TimelineItem[] => {
    const commentItems: TimelineItem[] = comments.map((c) => ({
        kind: "comment",
        at: new Date(c.createdAt).getTime(),
        comment: c,
    }));

    const rawEvents: RawEvent[] = [];
    for (const ev of events) {
        if (ev.addedAt) {
            rawEvents.push({
                kind: "added",
                at: new Date(ev.addedAt).getTime(),
                iso: ev.addedAt,
                who: ev.user,
                by: ev.addedBy,
            });
        }
        if (ev.removedAt) {
            rawEvents.push({
                kind: "removed",
                at: new Date(ev.removedAt).getTime(),
                iso: ev.removedAt,
                who: ev.user,
                by: ev.removedBy,
            });
        }
    }
    rawEvents.sort((a, b) => a.at - b.at);

    const all = [...commentItems, ...rawEvents.map((e) => ({ ...e, isRaw: true as const }))];
    all.sort((a, b) => a.at - b.at);

    const items: TimelineItem[] = [];
    let pending: { kind: "added" | "removed"; events: RawEvent[] } | null = null;

    const flushPending = () => {
        if (!pending || pending.events.length === 0) return;
        const last = pending.events[pending.events.length - 1];
        items.push({
            kind: pending.kind,
            at: last.at,
            changes: pending.events.map((e) => ({
                who: e.who,
                by: e.by,
                at: e.at,
                iso: e.iso,
            })),
            key: `${pending.kind}-${pending.events.map((e) => `${e.who.id}@${e.iso}`).join(",")}`,
        });
        pending = null;
    };

    for (const entry of all) {
        if ("isRaw" in entry) {
            if (pending && pending.kind === entry.kind) {
                pending.events.push(entry);
            } else {
                flushPending();
                pending = { kind: entry.kind, events: [entry] };
            }
        } else {
            flushPending();
            items.push(entry);
        }
    }
    flushPending();
    return items;
};

const formatChangeText = (kind: "added" | "removed", changes: ParticipantChange[]): string => {
    const verb = kind === "added" ? "added" : "removed";
    // Group by actor so we render "Alice added Bob, Carol" when one person did
    // multiple back-to-back actions.
    const byActor = new Map<string, { actorName: string | null; targets: string[] }>();
    for (const change of changes) {
        const actorKey = change.by ? `id:${change.by.id}` : "system";
        const actorName = change.by ? change.by.name : null;
        const entry = byActor.get(actorKey) ?? { actorName, targets: [] };
        entry.targets.push(change.who.name);
        byActor.set(actorKey, entry);
    }

    const formatList = (names: string[]): string => {
        if (names.length === 0) return "";
        if (names.length === 1) return names[0];
        if (names.length === 2) return `${names[0]} and ${names[1]}`;
        return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
    };

    const parts: string[] = [];
    for (const { actorName, targets } of byActor.values()) {
        const targetList = formatList(targets);
        if (actorName) {
            parts.push(`${actorName} ${verb} ${targetList}`);
        } else {
            // Subject-verb agreement for system-driven events (no actor): use
            // "was" for a single target, "were" for multiple.
            const auxiliary = targets.length === 1 ? "was" : "were";
            parts.push(`${targetList} ${auxiliary} ${verb}`);
        }
    }
    return parts.join(" · ");
};

export const DiscussionComments = ({
    className,
    comments,
    discussionId,
    threadId,
    participantEvents = [],
}: DiscussionCommentsProps) => {
    const [editingComment, setEditingComment] = useState<ApiThreadComment | null>(null);
    const [replyingToComment, setReplyingToComment] = useState<ApiThreadComment | null>(null);
    const [expandedCommentIds, setExpandedCommentIds] = useState<Set<number>>(new Set());
    const [deletingComment, setDeletingComment] = useState<ApiThreadComment | null>(null);

    const replyFormClearRef = useRef<(() => void) | null>(null);
    const editFormClearRef = useRef<(() => void) | null>(null);

    const currentUser = useCurrentUser();
    const timeZone = useUserTimezone();
    const utils = trpc.useUtils();
    const updateMutation = trpc.threads.updateComment.useMutation();
    const deleteMutation = trpc.threads.deleteComment.useMutation();
    const replyMutation = trpc.threads.createComment.useMutation();

    const shareUrlBuilder = (commentId: number) =>
        discussionId
            ? `${window.location.origin}/discussions/${discussionId}/${commentId}`
            : undefined;

    const handleReplyClose = useCallback(() => {
        if (replyingToComment) {
            // Auto-expand the parent comment's replies after submitting
            setExpandedCommentIds((prev) => new Set(prev).add(replyingToComment.id));
        }
        setReplyingToComment(null);
    }, [replyingToComment]);

    const timeline = useMemo(
        () => buildTimeline(comments, participantEvents),
        [comments, participantEvents],
    );

    return (
        <div className={cn("flex flex-col gap-6", className)}>
            {timeline.map((item) => {
                if (item.kind === "comment") {
                    const comment = item.comment;
                    return (
                        <div
                            key={`comment-${comment.id}`}
                            className="flex flex-col pb-6 last:border-none"
                        >
                            <UnifiedThreadComment
                                comment={comment}
                                context="discussion"
                                currentUser={currentUser}
                                onEdit={() => setEditingComment(comment)}
                                onDelete={() => setDeletingComment(comment)}
                                onReply={undefined}
                                shareUrl={shareUrlBuilder(comment.id)}
                                idPrefix="message"
                                repliesSlot={
                                    threadId && (
                                        <CommentReplies
                                            comment={comment}
                                            context="discussion"
                                            threadId={threadId}
                                            currentUser={currentUser}
                                            onReply={(c) => setReplyingToComment(c)}
                                            onEdit={(c) => setEditingComment(c)}
                                            shareUrlBuilder={shareUrlBuilder}
                                            forceExpanded={expandedCommentIds.has(comment.id)}
                                        />
                                    )
                                }
                            />
                            <Separator className="-mx-[24px] mt-3.5" />
                        </div>
                    );
                }

                const text = formatChangeText(item.kind, item.changes);
                const lastIso = item.changes[item.changes.length - 1].iso;
                const relative = formatRelativeTime(lastIso, timeZone);
                const tooltip = formatFullTimestamp(lastIso, timeZone);
                return (
                    <div key={item.key} className="flex justify-center">
                        <span
                            className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs italic text-gray-500"
                            title={tooltip}
                        >
                            <span>{text}</span>
                            <span className="not-italic text-gray-400">·</span>
                            <span className="not-italic text-gray-400">{relative}</span>
                        </span>
                    </div>
                );
            })}

            {editingComment && (
                <div className="fixed inset-0 z-30 bg-slate-900/20">
                    <div className="fixed left-1/2 top-1/2 z-40 w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-4">
                        <ThreadCommentForm
                            context="discussion"
                            mode="edit"
                            initialContentJson={editingComment.contentJson}
                            initialAttachments={editingComment.attachments}
                            clearFormRef={editFormClearRef}
                            onSubmit={({ contentJson, attachmentIds }) => {
                                updateMutation.mutate(
                                    {
                                        commentId: editingComment.id,
                                        contentJson,
                                        attachmentIds,
                                    },
                                    {
                                        onSuccess: () => {
                                            editFormClearRef.current?.();
                                            utils.threads.getComments.invalidate();
                                            utils.threads.getChildComments.invalidate();
                                            utils.discussions.list.invalidate();
                                            utils.discussions.getDiscussionView.invalidate({
                                                discussionId,
                                            });
                                            setEditingComment(null);
                                        },
                                    },
                                );
                            }}
                            onCancel={() => setEditingComment(null)}
                            showCancel
                            isLoading={updateMutation.isPending}
                            submitLabel="Save Changes"
                            placeholder="Edit your comment..."
                        />
                    </div>
                </div>
            )}
            {replyingToComment && threadId && (
                <ThreadCommentForm
                    context="discussion"
                    replyingToComment={replyingToComment}
                    clearFormRef={replyFormClearRef}
                    onSubmit={({ contentJson, attachmentIds }) => {
                        replyMutation.mutate(
                            {
                                threadId,
                                contentJson,
                                attachmentIds,
                                parentCommentId: replyingToComment.id,
                            },
                            {
                                onSuccess: () => {
                                    replyFormClearRef.current?.();
                                    utils.threads.getComments.invalidate();
                                    utils.threads.getChildComments.invalidate();
                                    utils.discussions.list.invalidate();
                                    handleReplyClose();
                                },
                            },
                        );
                    }}
                    onCancel={handleReplyClose}
                    showCancel
                    isLoading={replyMutation.isPending}
                    submitLabel="Send Message"
                    placeholder="Reply to this discussion"
                />
            )}

            {deletingComment && (
                <div className="fixed inset-0 z-30 bg-slate-900/20">
                    <div className="fixed left-1/2 top-1/2 z-40 w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-4">
                        <p className="mb-4 text-sm text-gray-700">
                            Are you sure you want to delete this comment?
                        </p>
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setDeletingComment(null)}
                                className="rounded px-3 py-1 text-sm text-gray-600 hover:bg-gray-100"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    deleteMutation.mutate(
                                        { commentId: deletingComment.id },
                                        {
                                            onSuccess: () => {
                                                utils.threads.getComments.invalidate();
                                                utils.discussions.list.invalidate();
                                                utils.discussions.getDiscussionView.invalidate({
                                                    discussionId,
                                                });
                                                setDeletingComment(null);
                                            },
                                        },
                                    );
                                }}
                                className="rounded bg-red-600 px-3 py-1 text-sm text-white hover:bg-red-700"
                                disabled={deleteMutation.isPending}
                            >
                                {deleteMutation.isPending ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
